import { useEffect, useRef, type RefObject } from "react";

/**
 * Development and staging guard for the dashboard's single-scroller rule
 * (see the comment on `.dashboard-content-panel` in root.tsx).
 *
 * Lint cannot catch this bug: it comes from layout, and the offending class can
 * arrive through a constant in another file. So this measures instead, and
 * logs a console error naming the route. It never runs in production.
 */

// Sub-pixel rounding between offsetHeight and scrollHeight.
const TOLERANCE_PX = 2;
// Lazy routes and their first data both land after navigation.
const CHECK_DELAYS_MS = [500, 2000];

/**
 * Returns why the page scrolls twice, or null. The panel's children are the
 * header, any banners, then the route last.
 *
 * The fingerprint of a 100% or viewport height on a route root is a panel that
 * overflows by exactly the height of everything above the route. A real long
 * page overflows by far more.
 */
export function detectDoubleScroll(
  panel: HTMLElement,
  doc: Document
): string | null {
  const children = Array.from(panel.children) as HTMLElement[];
  const chrome = children
    .slice(0, -1)
    .reduce((total, child) => total + child.offsetHeight, 0);
  const overflow = panel.scrollHeight - panel.clientHeight;
  // Routes that own their scroll (calendar, Insights) clip the panel instead.
  const panelScrolls =
    doc.defaultView?.getComputedStyle(panel).overflowY !== "hidden";

  if (
    panelScrolls &&
    chrome > 0 &&
    overflow > TOLERANCE_PX &&
    Math.abs(overflow - chrome) <= TOLERANCE_PX
  ) {
    return (
      `the content panel overflows by ${overflow}px, the height of the header and banners. ` +
      "The route root has a 100% or viewport height (h-full, min-h-full, h-screen, min-h-screen). Remove it."
    );
  }

  const root = doc.documentElement;
  const view = doc.defaultView;
  const locked =
    root.style.overflow === "hidden" ||
    doc.body.style.overflow === "hidden" ||
    (view && view.getComputedStyle(root).overflowY === "hidden");
  if (!locked && view && root.scrollHeight > view.innerHeight + TOLERANCE_PX) {
    return (
      "the document scrolls as well as the content panel. Look for an absolutely positioned " +
      "element with no positioned ancestor, or add `relative single-scroll-page` to the route root."
    );
  }

  return null;
}

export function useSingleScrollerCheck(
  panelRef: RefObject<HTMLElement | null>,
  pathname: string
) {
  const reported = useRef(new Set<string>());

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;

    const check = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const problem = detectDoubleScroll(panel, document);
      const key = `${pathname}|${problem}`;
      if (!problem || reported.current.has(key)) return;
      reported.current.add(key);
      console.error(`[single scroller] ${pathname}: ${problem}`);
    };

    const timers = CHECK_DELAYS_MS.map((delay) => setTimeout(check, delay));
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(check, 500);
    };
    window.addEventListener("resize", onResize);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, [panelRef, pathname]);
}
